import { z } from 'zod';

import { PAYMENT_TERMS } from '../constants/paymentTerms';
import { INVOICE_PAYMENT_STATUSES, INVOICE_STATUSES, INVOICE_TYPES } from '../types/invoice';

const isoDateTime = () => z.string().datetime({ offset: true });

export const invoiceSchema = z.object({
  id: z.string().uuid(),
  jobId: z.string().uuid(),
  status: z.enum(INVOICE_STATUSES),
  type: z.enum(INVOICE_TYPES),
  invoiceNumber: z.string().min(1),
  createdAt: isoDateTime(),
  updatedAt: isoDateTime(),
});

export const invoiceInputSchema = z.object({
  jobId: z.string().uuid(),
  status: z.enum(INVOICE_STATUSES),
  type: z.enum(INVOICE_TYPES),
  invoiceNumber: z.string().min(1),
});

export const invoiceUpdateSchema = invoiceInputSchema.partial().extend({
  jobId: z.string().uuid(),
});

// === NEW: Invoice document schemas (for the invoices table) ===

const baseInvoiceFields = {
  jobId: z.string().uuid().optional(),
  billId: z.string().uuid().nullable().optional(),
  invoiceDate: z.string().date().optional(),
  /**
   * Optional, not required: the API has callers older than this field (the
   * import scripts, the backfill), and an invoice with no terms is a state the
   * PDF and the overdue rule both already handle. The creation dialogs always
   * send one.
   */
  paymentTerms: z.enum(PAYMENT_TERMS).optional(),
};

// Discriminated by kind/source so line-item partials carry their selected
// line item ids while custom partials and finals keep the amount/description
// status quo.
//   - final: amount optional (backend pre-computes the active-doc total as a
//     suggestion; the user may override, e.g. when prior partials were paid).
//   - partial + custom (or no source): requires a positive amount.
//   - partial + lineItems: requires at least one selected line item id.
export const createInvoiceSchema = z
  .union([
    z.object({
      ...baseInvoiceFields,
      kind: z.literal('final'),
      amount: z.number().positive().optional(),
      description: z.string().optional(),
      // Finals can be cash too — a cash job's headline invoice is the whole
      // reason the job-level toggle exists. Same meaning as on the partial
      // arms: the INV-C<n> sequence and the cash payee line. Omitted here
      // originally, which made it unreachable for finals: zod strips unknown
      // keys, so a client that sent it got a silently non-cash invoice.
      isCash: z.boolean().optional(),
    }),
    z.object({
      ...baseInvoiceFields,
      kind: z.literal('partial'),
      source: z.literal('custom').optional(),
      amount: z.number().positive(),
      description: z.string().optional(),
      // Cash partials draw from a separate sequence and render as INV-C<n>.
      isCash: z.boolean().optional(),
    }),
    z.object({
      ...baseInvoiceFields,
      kind: z.literal('partial'),
      source: z.literal('lineItems'),
      lineItemIds: z.array(z.string().uuid()).min(1),
      description: z.string().optional(),
      isCash: z.boolean().optional(),
    }),
  ])
  .refine((d) => d.jobId != null || d.billId != null, {
    message: 'Either jobId or billId must be provided',
  });

export const updateInvoiceStatusSchema = z.object({
  status: z.enum(INVOICE_PAYMENT_STATUSES).optional(),
});

// Update payload for an existing invoice — used for internal notes and
// other mutable, post-creation fields. Kept separate from
// updateInvoiceStatusSchema so route handlers can be granular.
export const updateInvoiceSchema = z.object({
  notes: z.string().nullable().optional(),
  /**
   * The sent date, corrected by hand — for an invoice that went out some other
   * way (texted, handed over, mailed from a phone) and so was never stamped by
   * the send path.
   *
   * A DATE, not a timestamp, because that is what the office knows: which day
   * it went out, not which minute. The route anchors it inside the day — see
   * `setSentAtManually`, which is the one place that converts it.
   *
   * `null` clears it, which returns the invoice to Drafts (`sent_at IS NULL` is
   * that stage). The route refuses that on an invoice with money against it.
   */
  sentAt: z.string().date().nullable().optional(),
  /**
   * A free-form partial's customer-facing figure and text, corrected in place.
   *
   * FREE-FORM PARTIALS ONLY, and the route enforces it. A final's amount is
   * derived from its breakdown and a line-item partial's from its snapshot, so
   * letting either be typed over would print a total the rows beneath it do not
   * add up to. Those two change by regenerating.
   *
   * Both print on the PDF, so a change here drops the cached copy. The
   * alternative before this existed was void + delete + recreate, which burns
   * an invoice number to fix a typo.
   */
  amount: z.number().positive().optional(),
  description: z.string().nullable().optional(),
});

/**
 * The content of a partial invoice, replaced wholesale.
 *
 * Deliberately the same shape as `createInvoiceSchema`'s partial arms minus
 * `jobId` and `kind`: the Edit dialog IS the create dialog, so making the two
 * bodies diverge would mean two ways to describe one invoice. `kind` is absent
 * because a final is not editable — it is rebuilt by regenerating.
 *
 * `paymentTerms` is nullable here but not on create: an invoice that states no
 * terms is a real state (the PDF omits the row, and ageing falls back to
 * overdue_after_days), so an editor has to be able to express it.
 */
export const invoiceContentsSchema = z.union([
  z.object({
    source: z.literal('custom'),
    amount: z.number().positive(),
    description: z.string().nullable().optional(),
    paymentTerms: z.enum(PAYMENT_TERMS).nullable().optional(),
    isCash: z.boolean().optional(),
  }),
  z.object({
    source: z.literal('lineItems'),
    lineItemIds: z.array(z.string().uuid()).min(1),
    description: z.string().nullable().optional(),
    paymentTerms: z.enum(PAYMENT_TERMS).nullable().optional(),
    isCash: z.boolean().optional(),
  }),
]);

export type InvoiceContentsInput = z.infer<typeof invoiceContentsSchema>;

/**
 * The regenerate request body. Every field optional, and an absent body is
 * valid: regenerating with no options is the original behaviour (rebuild from
 * the current breakdown, change nothing else).
 *
 * `isCash` is the one creation-time choice regenerate can revise, because it
 * is the one the office cannot fix any other way short of void + reissue. It
 * carries the invoice NUMBER with it — see the route.
 */
export const regenerateInvoiceSchema = z.object({
  isCash: z.boolean().optional(),
  /**
   * Leave the invoice as it stands now behind as a VOIDED row on the job,
   * carrying the same number and the amount being replaced — so the list shows
   * that this job was once billed a different figure under that number.
   *
   * Absent means no copy, which is the behaviour every caller predating this
   * option already has. A cash recode leaves a voided row regardless, because
   * it retires a number and a retired number must still land on a row.
   */
  keepVoidedCopy: z.boolean().optional(),
});

export type RegenerateInvoiceInput = z.infer<typeof regenerateInvoiceSchema>;
